import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Card,
  Typography,
  Row,
  Col,
  AutoComplete,
  Tag
} from 'antd';
import { adminWordApi } from '@/services/api';

const { Title, Text } = Typography;

interface DiagramSectionProps {
  onCategoryChange?: (sectionIndex: number, category: string) => void;
  onWordChange?: (sectionIndex: number, words: string[]) => void;
  initialCategories?: string[];
  initialWords?: string[][];
}

const DiagramSection: React.FC<DiagramSectionProps> = ({
  onCategoryChange,
  onWordChange,
  initialCategories,
  initialWords
}) => {
  const [categories, setCategories] = useState<string[]>(initialCategories && initialCategories.length === 4 ? initialCategories : ['', '', '', '']);
  const [words, setWords] = useState<string[][]>(initialWords && initialWords.length === 4 ? initialWords : [[], [], [], []]);
  const [inputValues, setInputValues] = useState<string[]>(['', '', '', '']);
  const [optionsList, setOptionsList] = useState<Array<Array<{ key: string; value: string; label: string; item: any }>>>([[], [], [], []]);

  // 초기 데이터 폼 바인딩
  const form = Form.useFormInstance();
  React.useEffect(() => {
    const nextCats = (initialCategories && initialCategories.length === 4) ? initialCategories : ['', '', '', ''];
    const nextWords = (initialWords && initialWords.length === 4) ? initialWords : [[], [], [], []];
    setCategories(nextCats);
    setWords(nextWords);
    const values: Record<string, any> = {};
    nextCats.forEach((c, idx) => values[`category_${idx}`] = c || '');
    form.setFieldsValue(values);
    // 단어는 태그로 표시하므로 폼 필드는 굳이 저장하지 않음
  }, [initialCategories, initialWords, form]);

  const handleCategoryChange = (sectionIndex: number, value: string) => {
    const newCategories = [...categories];
    newCategories[sectionIndex] = value;
    setCategories(newCategories);
    onCategoryChange?.(sectionIndex, value);
  };

  const handleWordInputChange = (sectionIndex: number, value: string) => {
    const newInputValues = [...inputValues];
    newInputValues[sectionIndex] = value;
    setInputValues(newInputValues);
  };

  const handleWordSelect = (sectionIndex: number, value: string) => {
    const newWords = [...words];
    if (!newWords[sectionIndex].includes(value)) {
      newWords[sectionIndex] = [...newWords[sectionIndex], value];
      setWords(newWords);
      onWordChange?.(sectionIndex, newWords[sectionIndex]);
    }
    
    // 입력값 초기화
    const newInputValues = [...inputValues];
    newInputValues[sectionIndex] = '';
    setInputValues(newInputValues);
  };

  const handleWordRemove = (sectionIndex: number, wordIndexToRemove: number) => {
    const newWords = [...words];
    newWords[sectionIndex] = newWords[sectionIndex].filter((_, idx) => idx !== wordIndexToRemove);
    setWords(newWords);
    onWordChange?.(sectionIndex, newWords[sectionIndex]);
  };

  const buildOptions = (list: any[]) => {
    const toKo = {
      gender: (v: string) => v === 'male' ? '남성' : v === 'female' ? '여성' : v,
      age: (v: string) => (
        v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v
      ),
      speed: (v: string) => v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v,
      pitch: (v: string) => v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v,
    } as const;
    return list.map((w: any) => {
      const g = toKo.gender(String(w.gender || ''));
      const a = toKo.age(String(w.age || ''));
      const s = toKo.speed(String(w.speed || ''));
      const p = toKo.pitch(String(w.pitch || ''));
      return {
        key: `${w.id}-${w.gender}-${w.age}-${w.speed}-${w.pitch}`,
        value: String(w.word || ''),
        label: `${w.word}(${g}|${a}|${s}|${p})`,
        item: w,
      };
    });
  };

  const searchWords = (sectionIndex: number, query: string) => {
    window.clearTimeout((searchWords as any)._t);
    (searchWords as any)._t = window.setTimeout(async () => {
      try {
        if (!query) {
          const pageSize = 500;
          let page = 0;
          let all: any[] = [];
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const res = await adminWordApi.list({ q: '', page, size: pageSize });
            const data = res?.data || res;
            const content = data?.content || data?.data?.content || [];
            all = all.concat(content);
            if (content.length < pageSize || all.length >= 2000) break;
            page += 1;
          }
          setOptionsList(prev => {
            const next = [...prev];
            next[sectionIndex] = buildOptions(all);
            return next;
          });
        } else {
          const res = await adminWordApi.list({ q: query, page: 0, size: 500 });
          const data = res?.data || res;
          const content = data?.content || data?.data?.content || [];
          const filtered = content.filter((w: any) => String(w.word || '').toLowerCase().startsWith(query.toLowerCase()));
          setOptionsList(prev => {
            const next = [...prev];
            next[sectionIndex] = buildOptions(filtered);
            return next;
          });
        }
      } catch (_) {
        setOptionsList(prev => {
          const next = [...prev];
          next[sectionIndex] = [];
          return next;
        });
      }
    }, 250);
  };

  const renderSection = (sectionIndex: number) => {
    return (
      <div key={sectionIndex} className='p-2 border-gray-200'>
        {/* <div className='mb-4'>
          <Text strong className='text-lg'>섹션 {sectionIndex + 1}</Text>
        </div> */}
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              label="카테고리"
              name={`category_${sectionIndex}`}
              rules={[
                { required: true, message: '카테고리를 입력해주세요' }
              ]}
            >
              <Input
                placeholder="카테고리를 입력하세요"
                size="large"
                value={categories[sectionIndex]}
                onChange={(e) => handleCategoryChange(sectionIndex, e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="등록할 단어들"
              name={`word_${sectionIndex}`}
            >
              <div>
                <AutoComplete
                  placeholder="등록된 단어를 입력하세요"
                  size="large"
                  value={inputValues[sectionIndex]}
                  onChange={(value) => handleWordInputChange(sectionIndex, value)}
                  onSelect={(value) => handleWordSelect(sectionIndex, value)}
                  onFocus={() => searchWords(sectionIndex, '')}
                  onSearch={(q) => searchWords(sectionIndex, q)}
                  options={optionsList[sectionIndex]}
                  filterOption={false}
                />
                <div className='mt-2'>
                  {words[sectionIndex].map((word, wordIndex) => (
                    <Tag
                      key={`${sectionIndex}-${word}-${wordIndex}`}
                      closable
                      onClose={(e) => { e.preventDefault(); handleWordRemove(sectionIndex, wordIndex); }}
                      className='mb-1'
                    >
                      {word}
                    </Tag>
                  ))}
                </div>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Card className='pb-5 border-b border-gray-200'>
      <Title level={4} className='mb-4'>다이어그램</Title>
      
      {/* 4개 섹션 */}
      <div>
        {[0, 1, 2, 3].map(sectionIndex => renderSection(sectionIndex))}
      </div>
    </Card>
  );
};

export default DiagramSection; 